import { cloneDeep, isNumber, pull, pullAll, remove } from 'lodash';
import { v4 as uuid } from 'uuid';
import { create } from 'zustand';
import { IExportState, ILayerProps, StateType } from '@/version-types';
import { CoreAPI, getBehavior, getState, useState } from '../../core';
import { exportLayerOriginInfo, StageAPI } from '../../stage';
import { delActiveInfo, getActiveInfo, setActiveInfo } from '../tools/active-info';
import { delSelectedInfo, setSelectedInfo } from '../tools/selected-info';
import { IDelLayerData } from '../type';
import { getEditorOperateState } from './editor-operate-state';

/** 状态 */
interface IState {
    /** 悬浮操作的图层id */
    hoverId: string;
    /** 激活的图层信息key，真实数据在 instance-data 中 */
    activeInfoKey: string;
    /** 选中图层信息key，真实数据在 instance-data 中 */
    selectedInfoKey: string;
}

/** 动作 */
interface IAction {
    /** 设置悬浮信息 */
    setHoverId: (hoverId: string) => void;
    /** 设置激活图层 */
    setActiveLayer: (layerId: string, ignoreDisableOperate?: boolean) => void;
    /** 删除激活图层 */
    delActiveLayer: (autoRerender?: boolean) => void;
    /** 获取激活图层 */
    getActiveLayer: () => ILayerProps | null;
    /**
     * 获取激活图层的选中图层，
     * 如果 force 为 true，没有选中图层时，会返回激活图层
     */
    getActivePickLayer: (force?: boolean) => ILayerProps | null;
    /** 删除图层 */
    delLayer: (layerId: string) => IDelLayerData | null;

    /** 清除选中图层 */
    clearSelected: () => void;
    /** 设置选中图层 */
    setSelected: (args: string[] | string) => void;

    /** 销毁 */
    destroy: () => void;
}

/** 存储类型 */
interface IStore extends IState, IAction {}

/** 初始化状态 */
function getInitialState() {
    return {
        hoverId: '',
        activeInfoKey: '',
        selectedInfoKey: '',
    };
}

/** 创建存储 */
const createState = (identity: string) => {
    const store = create<IStore>((set, get) => ({
        ...getInitialState(),

        destroy() {
            delActiveInfo(identity, get().activeInfoKey);
            delSelectedInfo(identity, get().selectedInfoKey);
            set(getInitialState());
        },

        clearSelected() {
            delSelectedInfo(identity, get().selectedInfoKey);
            set({ selectedInfoKey: '' });
        },

        setSelected(args) {
            const isArray = Array.isArray(args);
            const key = uuid();
            delSelectedInfo(identity, get().selectedInfoKey);
            const idsSet = new Set<string>();
            if (isArray) {
                args.forEach((id) => {
                    idsSet.add(id);
                });
            } else {
                idsSet.add(args);
            }
            setSelectedInfo(identity, key, idsSet);
            set({ selectedInfoKey: key });
        },

        setHoverId(hoverId) {
            const editorOperate = getEditorOperateState(identity);
            if (editorOperate.disableOperate) return;
            if (editorOperate.isBaseOperate && hoverId) return;
            set({ hoverId });
        },

        setActiveLayer(layerId, ignoreDisableOperate = false) {
            const editorOperate = getEditorOperateState(identity);
            if (editorOperate.disableOperate && !ignoreDisableOperate) return;
            if (editorOperate.disableActive) return;

            const coreApi = CoreAPI(identity);
            const layer = coreApi.getLayer(layerId);
            const oldKey = get().activeInfoKey;
            const oldActiveInfo = getActiveInfo(identity, oldKey);
            delActiveInfo(identity, oldKey);

            if (!layer || getBehavior(identity, layer)?.checkDisableActive?.(layer)) {
                set({ activeInfoKey: '' });
                return;
            }

            const warpLayer = coreApi.getWrapLayer(layerId);
            let activeLayerId = warpLayer?.id || '';

            if (oldActiveInfo?.info.id && (layerId !== warpLayer?.id || warpLayer?.id !== oldActiveInfo.info.id)) {
                const oldLayer = coreApi.getLayer(oldActiveInfo.info.id);
                if (oldLayer) {
                    const isRemoveGroup = coreApi.tryRemoveGroup(oldLayer, true);
                    if (isRemoveGroup) {
                        activeLayerId = layerId;
                    }
                }
            }

            const { originInfo, childOriginInfoMap } = exportLayerOriginInfo(identity, activeLayerId);

            if (!originInfo) {
                set({ activeInfoKey: '' });
                return;
            }

            originInfo.pickId = activeLayerId !== layerId ? layerId : '';

            const newKey = uuid();
            setActiveInfo(identity, newKey, {
                info: originInfo,
                childInfoMap: childOriginInfoMap,
            });

            set({ activeInfoKey: newKey });
        },

        delLayer(layerId: string) {
            const coreApi = CoreAPI(identity);
            const layer = coreApi.getLayer(layerId, true);
            if (!layer || layer.lock) return null;
            const originIndex = coreApi.tryGetLayerOriginIndex(layerId);
            if (!isNumber(originIndex)) return null;
            const renderIds = coreApi.getRenderIds(originIndex);
            const flatLayersMap = coreApi.getFlatLayersMap();
            const warpLayer = coreApi.getWrapLayer(layerId);

            /** 向上递归删除只有0/1个元素的组 */
            function updateParentLayer(layer: ILayerProps, layerId: string): ILayerProps {
                if (layerId) {
                    // console.log('ot updateParentLayer', cloneDeep({ layer, layerId }));
                    remove(layer.children || [], (item) => item.id === layerId);
                    flatLayersMap.delete(layerId);
                    pull(renderIds, layerId);
                }
                const childCount = layer.children?.length ?? 0;
                const parentId = layer.parentId;

                if (parentId) {
                    const parentLayer = coreApi.getLayer(parentId);

                    if (childCount === 0) {
                        remove(parentLayer.children || [], (item) => item.id === layer.id);
                        flatLayersMap.delete(layer.id);
                        pull(renderIds, layer.id);
                        return updateParentLayer(parentLayer, '');
                    } else if (childCount === 1) {
                        const childLayer = layer.children[0];
                        const layerIndex = (parentLayer.children || []).findIndex((item) => item.id === layer.id);
                        if (layerIndex !== -1) {
                            childLayer.parentId = parentLayer.id;
                            parentLayer.children[layerIndex] = childLayer;
                            flatLayersMap.delete(layer.id);
                            pull(renderIds, layer.id);
                            return updateParentLayer(parentLayer, '');
                        }
                    }
                }

                return layer;
            }

            // 删除子图层
            if (warpLayer?.id !== layerId && layer.parentId) {
                coreApi.tryRemoveGroup(coreApi.getWrapLayer(layerId, true), false);
                const oldLayer = cloneDeep(warpLayer) as ILayerProps;
                const parentLayer = updateParentLayer(coreApi.getLayer(layer.parentId), layerId);
                const parentIndex = renderIds.indexOf(parentLayer.id);
                const childCount = parentLayer.children?.length ?? 0;

                if (childCount === 0) {
                    // 没有子图层，删除组
                    const activeKey = get().activeInfoKey;
                    const activeInfo = getActiveInfo(identity, activeKey);
                    const activeId = activeInfo?.info.id;
                    if (activeId === parentLayer.id) {
                        delActiveInfo(identity, activeKey);
                        set({ activeInfoKey: '' });
                    }
                    // const parentIndex = renderIds.indexOf(parentLayer.id);
                    pull(renderIds, parentLayer.id);
                    flatLayersMap.delete(parentLayer.id);
                    coreApi.rerender();
                    return {
                        item: {
                            oldLayer,
                            oldLayerIndex: parentIndex,
                            replaceLayerId: '',
                            delId: layerId,
                            childIndexes: [],
                            childIds: [],
                            delAll: false,
                        },
                        operate: {
                            activeId: '',
                        },
                    };
                } else if (childCount === 1) {
                    // 剩一个，删除组，替换为子图层
                    const childLayer = parentLayer.children[0];
                    childLayer.parentId = undefined;
                    // const parentIndex = renderIds.indexOf(parentLayer.id);
                    renderIds.splice(parentIndex, 1, childLayer.id);
                    const layer = cloneDeep(childLayer);
                    flatLayersMap.delete(parentLayer.id);
                    pull(renderIds, parentLayer.id);
                    StageAPI(identity).addOrUpdate(layer, false);
                    coreApi.rerender();
                    return {
                        item: {
                            oldLayer,
                            oldLayerIndex: parentIndex,
                            replaceLayerId: childLayer.id,
                            delId: layerId,
                            childIndexes: [],
                            childIds: [],
                            delAll: false,
                        },
                        operate: {
                            activeId: childLayer.id,
                        },
                    };
                } else {
                    // 剩多个子图层
                    const newLayer = cloneDeep(parentLayer);
                    StageAPI(identity).addOrUpdate(cloneDeep(parentLayer), false);
                    coreApi.rerender();
                    return {
                        item: {
                            newLayer,
                            oldLayer,
                            oldLayerIndex: parentIndex,
                            replaceLayerId: '',
                            delId: layerId,
                            childIndexes: [],
                            childIds: [],
                            delAll: false,
                        },
                        operate: {
                            activeId: oldLayer?.id || '',
                        },
                    };
                }
            } else if (warpLayer?.id === layerId) {
                // 删除最外层
                const activeKey = get().activeInfoKey;
                const activeInfo = getActiveInfo(identity, activeKey);
                const activeId = activeInfo?.info.id;

                if (layerId === activeId) {
                    delActiveInfo(identity, activeKey);
                    set({ activeInfoKey: '' });
                }

                const isExtraGroup = coreApi.checkIsExtraGroup(layer);
                const childIds = isExtraGroup ? layer?.children?.map((item) => item.id) : [];
                const childIndexes = childIds.map((id) => renderIds.indexOf(id));
                const layerIndex = renderIds.indexOf(layerId);

                const delIds = [...childIds, layerId];
                pullAll(renderIds, delIds);
                delIds.forEach((id) => {
                    flatLayersMap.delete(id);
                });

                coreApi.rerender();

                return {
                    item: {
                        oldLayer: layer,
                        oldLayerIndex: layerIndex,
                        replaceLayerId: '',
                        delId: layerId,
                        childIndexes,
                        childIds,
                        delAll: true,
                    },
                    operate: {
                        activeId: '',
                    },
                };
            }
            return null;
        },

        delActiveLayer(autoRerender = true) {
            const coreApi = CoreAPI(identity);
            const activeLayer = get().getActiveLayer();
            let isRemoveGroup = false;
            if (activeLayer) {
                isRemoveGroup = coreApi.tryRemoveGroup(activeLayer) ?? false;
            }

            const oldKey = get().activeInfoKey;
            delActiveInfo(identity, oldKey);

            if (autoRerender) {
                set({ activeInfoKey: '' });
            }
            if (autoRerender && isRemoveGroup) {
                coreApi.rerender();
            }
        },

        getActiveLayer() {
            const activeInfo = getActiveInfo(identity, get().activeInfoKey);
            if (!activeInfo?.info.id) return null;
            return CoreAPI(identity).getLayer(activeInfo?.info.id);
        },

        getActivePickLayer(force = true) {
            const activeInfo = getActiveInfo(identity, get().activeInfoKey);
            if (!activeInfo?.info.id) return null;
            return CoreAPI(identity).getLayer(
                force ? activeInfo?.info.pickId || activeInfo.info.id : activeInfo?.info.pickId,
            );
        },
    }));

    return store;
};

/** 存储名称 */
const name = 'layer-operate-state';

/** 存储类型 */
type ModuleType = StateType<IStore, typeof name>;

/** hook 获取存储 */
export const useLayerOperateState = () => {
    return useState<ModuleType>()[name];
};

/** 通过 id 获取存储 */
export const getLayerOperateState = (identity: string) => {
    return getState<ModuleType>(identity)[name].getState();
};

const exportState: IExportState = {
    name,
    createState,
};

export default exportState;
